const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/defs/generalDefs.ts';
import { MessageDescriptor } from 'react-intl';
import { PrimitiveType } from 'intl-messageformat';
import pck from '../../package.json';

export const COMMON_WEB_VERSION = pck.version;

export type SupportedLocale = 'en' | 'de' | 'fr' | 'it';

export interface KeyValuePair<T = any> {
    [key: string]: T;
}

/**
 * Recursively makes all properties of T optional.
 */
export type RecursivePartial<T> = {
    [P in keyof T]?: T[P] extends (infer U)[]
        ? RecursivePartial<U>[]
        : T[P] extends object | undefined
          ? RecursivePartial<T[P]>
          : T[P];
};

export interface KeyStringValuePair extends KeyValuePair<string> {}

export interface ValueSet extends KeyValuePair<string | undefined> {}

export const kisimFontSizes = {
    veryHuge: '1.35rem',
    huge: '1.25rem',
    larger: '1.14rem',
    large: '1.08rem',
    medium: '1rem',
    small: '0.92rem',
    smaller: '0.84rem',
    tiny: '0.75rem',
};

/**
 *  According to Material Design, text should be dimmed with opacity depending on what the text stands for.
 */
export const opacity = {
    // Text over a light background:
    primarytextOnLt: 0.87, /// primary text
    secondarytextOnLt: 0.54, /// secondary text (e.g. captions)
    inactivetextOnLt: 0.26, /// inactive text
    hinttextOnLt: 0.26, /// hints and placeholders
    iconOnLt: 0.54, /// icons in general
    inactiveiconOnLt: 0.26, /// inactive icons in general (use for icons list items)
    dividerOnLt: 0.12, /// separator lines

    // Text over a dark background:
    primarytextOnDk: 1, /// primary text
    secondarytextOnDk: 0.7, /// secondary text (e.g. captions)
    inacitvetextOnDk: 0.3, /// inactive text
    hinttextOnDk: 0.3, /// hints and placeholders
    iconOnDk: 1, /// icons in general
    dividerOnDk: 0.12, /// separator lines
};

// Default time restrictions for events
export const DEFAULT_MIN_DAYS = 30;
export const DEFAULT_MAX_HOURS = 1;

// DO NOT - UNDER NO CIRCUMSTANCE - EVER CHANGE THESE CONSTANTS :< ALMOST EVERYTHING WILL BREAK IF YOU DO!
export const KISIM_DATE_FORMAT = 'YYYY-MM-DD';
export const KISIM_TIME_FORMAT = 'YYYY-MM-DD HH:mm:ss';
export const KISIM_STD_DATE_FORMAT = 'DD.MM.YYYY';
export const KISIM_STD_DATE_TIME_FORMAT = 'DD.MM.YYYY HH:mm';
export const KISIM_STD_DATE_TIME_FORMAT_WITH_COMMA = 'DD.MM.YYYY, HH:mm';
export const KISIM_STD_SHORT_DATE_TIME_FORMAT = 'DD.MM. HH:mm';
export const KISIM_STD_TIMEONLY_FORMAT = 'HH:mm';
export const KISIM_STD_FULLDAY_FORMAT = 'dddd';

/**
 * Time string of KISIM for dates which are not set (open)
 */
export const KISIM_TIME_OPEN = '3000-01-01 00:00:00';

/**
 * Time string of KISIM for lower limit
 */
export const KISIM_TIME_LOWER_LIMIT = '1900-01-01 00:00:00';

/**
 * Mui's internal date/time format. Do not change!
 * A DatePicker's inputFormat must always be MUI_INTERNAL_DATE_FORMAT because of this bug:
 * https://github.com/mui-org/material-ui-pickers/issues/1776
 */
export const MUI_INTERNAL_DATE_FORMAT = 'MM/DD/YYYY';

/**
 * union types for data coming from an remote api
 */

interface NotLoaded {
    readonly kind: 'notLoaded';
}

interface Loading {
    readonly kind: 'loading';
}

interface Failure<E> {
    readonly kind: 'failure';
    value: E;
}

interface Success<T> {
    readonly kind: 'success';
    value: T;
}

export namespace RemoteData {
    export interface NotLoaded {
        readonly kind: 'notLoaded';
    }

    export interface Loading {
        readonly kind: 'loading';
    }

    export interface Failure<E> {
        readonly kind: 'failure';
        value: E;
    }

    export interface Success<T> {
        readonly kind: 'success';
        value: T;
    }

    export const notLoaded = (): NotLoaded => ({ kind: 'notLoaded' });
    export const loading = (): Loading => ({ kind: 'loading' });
    export const failure = <T, E>(message: E): RemoteData<T, E> => ({ kind: 'failure', value: message });
    export const success = <T, E>(data: T): RemoteData<T, E> => ({ kind: 'success', value: data });
}

// eslint-disable-next-line no-redeclare
export type RemoteData<T, E> = NotLoaded | Loading | Failure<E> | Success<T>;

/**
 * An event is considered to match a hotspot, if the time difference between the hotspot and the event is smaller than this value. Apart
 * from the time, there is more which has to match (i.e. the event must refer to the order of the hotspot).
 */
export const HOTSPOT_MATCH_MAX_DIFF = 15 * 60; /// 15 minutes

export type FormatMessageFn = (descriptor: MessageDescriptor, values?: Record<string, PrimitiveType>) => string;

export enum AsyncProcessStatus {
    IDLE = 'IDLE',
    PENDING = 'PENDING',
    SUCCESS = 'SUCCESS',
    FAILURE = 'FAILURE',
}
